import clsx from "clsx";
import { useMemo } from "react";

import {
  useCurrentWorkspace,
  useSessionUser,
  useWorkspaceConnections,
} from "@web-app/api";
import { Avatar, CopyableField, DateTimeTz, Icon } from "@web-app/components";
import StatusCell from "@web-app/pages/manage/Table/StatusCell";
import { Order, Route } from "@web-app/types";
import {
  generateTrackingLink,
  getMetersToMiles,
  getProperWsCustomDomainAndId,
  pluralize,
} from "@web-app/utils/helpers";
import { getUserReadableTimestringFromSeconds } from "@web-app/utils/time";

import useRouteDetailsTab from "./useRouteDetailsTab";

export default function RouteDetailsTab({
  route,
}: {
  route: {
    details: Route | null;
    orders: Order[];
  } | null;
}) {
  const {
    firstOrder,
    lastOrder,
    timezone,
    getStatus,
    getActualDistance,
    getRouteAssignee,
  } = useRouteDetailsTab({ route });
  const {
    data: workspaceConnections,
    isLoading: isLoadingWorkspaceConnections,
  } = useWorkspaceConnections();

  const { data: currentWorkspace } = useCurrentWorkspace();

  const { data: sessionUser, isLoading: isLoadingSessionUser } =
    useSessionUser();

  const routeAssignee = getRouteAssignee();

  const [orderWsCustomDomain, routeId] = useMemo<string[]>(() => {
    if (!isLoadingWorkspaceConnections && !isLoadingSessionUser) {
      const firstOrder = route?.orders[0];
      const routeId = route?.details?.id as string;
      const orderWsId = firstOrder?.workspaceId;
      const orderConnWsId = firstOrder?.connectedWorkspaceId ?? null;

      if (!firstOrder || !workspaceConnections || !sessionUser || !orderWsId)
        return ["", ""];

      return getProperWsCustomDomainAndId(
        workspaceConnections,
        orderWsId,
        orderConnWsId,
        routeId,
        sessionUser?.currentWorkspaceId === firstOrder.workspaceId,
        currentWorkspace?.customDomain || "",
      );
    }

    return ["", ""];
  }, [
    workspaceConnections,
    sessionUser,
    currentWorkspace,
    isLoadingWorkspaceConnections,
    isLoadingSessionUser,
    route,
  ]);

  const routeDuration = route?.details?.routeDuration;
  if (!route) return null;
  if (!route.details) return null;

  const completedOrders = route.orders.filter(
    (order) =>
      order.status === "COMPLETE" ||
      order.status === "CANCELLED" ||
      order.status === "FAILED",
  );
  const incompleteOrders = route.orders.filter(
    (order) =>
      order.status !== "COMPLETE" &&
      order.status !== "CANCELLED" &&
      order.status !== "FAILED",
  );

  const isComplete = route.details.status === "COMPLETE";

  const actualCompletedTimestamp = route.details.completedAt;

  const routeCompletedAtTime = actualCompletedTimestamp
    ? new Date(actualCompletedTimestamp)
    : null;
  return (
    <div className="flex flex-col relative z-30">
      <div className="flex flex-col gap-2 py-5 border-b border-gray-200 px-4">
        <div className="grid grid-cols-2 gap-4">
          <span className="tw-details-field-title">Status</span>
          <span className="tw-details-field-value font-semibold w-fit">
            <StatusCell value={getStatus()} />
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <span className="tw-details-field-title">Route ID</span>
          <span className="tw-details-field-value">{route.details.id}</span>
        </div>
      </div>
      <div className="flex flex-col gap-2 py-5 border-b border-gray-200 px-4">
        {route.details.estimatedPickupTime && (
          <div className="grid grid-cols-2 gap-4">
            <span className="tw-details-field-title">
              Estimated Pickup Time
            </span>
            <span className="tw-details-field-value">
              <DateTimeTz
                start={new Date(route.details.estimatedPickupTime)}
                timezone={timezone}
                presentation="default"
              />
            </span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <span className="tw-details-field-title">Estimated Distance</span>
          <span className="tw-details-field-value flex items-center gap-2">
            {route.details.estimatedDistance
              ? `${getMetersToMiles(route.details.estimatedDistance)} miles`
              : "Not available"}
          </span>
        </div>
        {isComplete && (
          <div className="grid grid-cols-2 gap-4">
            <span className="tw-details-field-title">Actual Distance</span>
            <span className="tw-details-field-value flex items-center gap-2">
              {`${getActualDistance()} miles`}
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 py-5 border-b border-gray-200 px-4">
        <div className="grid grid-cols-2 gap-4">
          <span className="tw-details-field-title">Scheduled Start Time</span>
          <span className="tw-details-field-value">
            {firstOrder?.scheduledStartDateTime ? (
              <DateTimeTz
                start={new Date(firstOrder.scheduledStartDateTime)}
                timezone={timezone}
                presentation="default"
              />
            ) : (
              "Not available"
            )}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <span className="tw-details-field-title">Scheduled End Time</span>
          <span className="tw-details-field-value">
            {lastOrder?.scheduledEndDateTime ? (
              <DateTimeTz
                start={new Date(lastOrder.scheduledEndDateTime)}
                timezone={timezone}
                presentation="default"
              />
            ) : (
              "Not available"
            )}
          </span>
        </div>
        {isComplete && route?.details.startedAt && (
          <div className="grid grid-cols-2 gap-4">
            <span className="tw-details-field-title">Actual Start Time</span>
            <span className="tw-details-field-value">
              <DateTimeTz
                start={new Date(route?.details.startedAt)}
                timezone={timezone}
                presentation="default"
              />
            </span>
          </div>
        )}
        {isComplete && routeCompletedAtTime && (
          <div className="grid grid-cols-2 gap-4">
            <span className="tw-details-field-title">
              Actual End Time / Completed At
            </span>
            <span className="tw-details-field-value">
              <DateTimeTz
                start={routeCompletedAtTime}
                timezone={timezone}
                presentation="default"
              />
            </span>
          </div>
        )}
        {!isComplete && lastOrder?.eta?.dateTime && (
          <div className="grid grid-cols-2 gap-4">
            <span className="tw-details-field-title">Estimated At</span>
            <span className="tw-details-field-value">
              <DateTimeTz
                start={new Date(lastOrder.eta.dateTime)}
                timezone={timezone}
                presentation="default"
              />
            </span>
          </div>
        )}
        {isComplete && routeDuration && (
          <div className="grid grid-cols-2 gap-4">
            <span className="tw-details-field-title">Duration</span>
            <span className="tw-details-field-value">
              {getUserReadableTimestringFromSeconds(routeDuration)}
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 py-5 border-b border-gray-200 px-4">
        <span className="tw-details-field-title">Assignee</span>
        <span className="tw-details-field-value font-semibold flex flex-col gap-2">
          <span className="flex items-center gap-2">
            {!routeAssignee && <span>No Assignee</span>}
            {routeAssignee && (
              <>
                <span>
                  <Avatar image={routeAssignee?.image ?? undefined} size={10} />
                </span>
                <span className="flex flex-col">
                  <span className="font-semibold text-gray-700">
                    {routeAssignee.name}
                  </span>
                  <CopyableField
                    icon="fasPhone"
                    value={routeAssignee?.phone ?? ""}
                    appearance="inline"
                    className="text-xs font-normal"
                  />
                </span>
              </>
            )}
          </span>
        </span>
      </div>
      <div className="flex flex-col gap-2 py-3 border-b border-gray-200 px-4">
        <div className="grid grid-cols-2 gap-4">
          <span className="tw-details-field-title">No. of Orders</span>
          <span className="tw-details-field-value">
            {`${route.orders.length} ${pluralize(
              "order",
              route.orders.length,
            )}`}
          </span>
        </div>
        {!isComplete && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <span className="tw-details-field-title">
                No. of Completed Orders
              </span>
              <span className="tw-details-field-value flex items-center gap-2">
                {`${completedOrders.length} ${pluralize(
                  "order",
                  completedOrders.length,
                )}`}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <span className="tw-details-field-title">
                No. of Incomplete Orders
              </span>
              <span className="tw-details-field-value flex items-center gap-2">
                {`${incompleteOrders.length} ${pluralize(
                  "order",
                  incompleteOrders.length,
                )}`}
              </span>
            </div>
          </>
        )}
        <div className="grid grid-cols-2 gap-4">
          <span className="tw-details-field-title">Order Sequence</span>
          <span className="tw-details-field-value flex items-center gap-2">
            <Icon
              name={route.details.isFlexible ? "fasShuffle" : "listTree"}
              className="w-4 h-4 text-gray-400 font-semibold"
            />
            {route.details.isFlexible ? "Flexible" : "Ordered"}
          </span>
        </div>
        <div
          className={clsx("grid grid-cols-2 gap-4", {
            hidden: !orderWsCustomDomain && !routeId,
          })}
        >
          <span className="tw-details-field-title">Tracking Link</span>
          <span
            className="tw-details-field-value"
            style={{ overflowWrap: "anywhere" }}
          >
            <CopyableField
              value={generateTrackingLink(orderWsCustomDomain, routeId)}
              appearance="link-inline"
              className="text-xs font-normal"
            />
          </span>
        </div>
      </div>
    </div>
  );
}
